import React, { createContext, useContext, useState, useEffect } from 'react';
import { account, getCurrentUser, signOut as appwriteSignOut, clearUserSession } from '../services/appwrite';
import { userProfileMarkAccepted } from '../services/api';
import { toast } from 'sonner';
import { isDevModeEnabled } from '../services/devAuth';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [authError, setAuthError] = useState(null);

  useEffect(() => {
    // Get initial session
    const initializeAuth = async () => {
      try {
        // Dev mode: create real Appwrite session with dev credentials
        if (isDevModeEnabled()) {
          const { createDevSession } = await import('../services/devAuth');
          try {
            console.log('[Dev Mode] Initializing dev authentication...');
            const devSession = await createDevSession();
            setSession(devSession);
            
            // Get user details normally (now that we have a real session)
            const currentUser = await getCurrentUser();
            if (currentUser) {
              console.log('[Dev Mode] User loaded:', currentUser.email);
              setUser(currentUser);
            } else {
              toast.error('[Dev Mode] User not found in database');
            }
          } catch (error) {
            console.error('[Dev Mode] Failed to authenticate:', error);
            toast.error(`Dev mode authentication failed: ${error.message}`);
          }
          setLoading(false);
          return;
        }

        // Normal authentication flow
        if (window.location.pathname === '/auth/callback') {
          console.log('On auth callback page, skipping auth initialization');
          setLoading(false);
          return;
        }

        // Check if there's an active Appwrite session
        try {
          const authSession = await account.getSession('current');
          console.log('Initial session check:', authSession);
          
          if (authSession) {
            setSession(authSession);
            
            // Then get user details from database
            const currentUser = await getCurrentUser();
            if (currentUser) {
              console.log('Initial user loaded:', currentUser);
              setUser(currentUser);
            } else {
              // User has Appwrite session but not in database
              if (window.location.pathname !== '/auth/callback') {
                toast.error('Account not set up. Please contact administrator.');
                await appwriteSignOut();
              }
            }
          }
        } catch (error) {
          // No active session - this is normal for unauthenticated users
          console.log('No active session');
        }
      } catch (error) {
        console.error('Failed to initialize auth:', error);
        setAuthError('Authentication failed');
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();

    // Note: Appwrite doesn't have a built-in auth state listener like Supabase
    // For auth state changes, we need to use Realtime subscriptions or handle manually
    // OAuth callbacks will redirect and trigger a page reload, which will run initializeAuth again
    
  }, []);

  // An invited person's first sign-in into an organisation is what accepts the
  // invitation (#68): it clears the "Invited" badge in User settings. Fire and
  // forget — it never blocks or fails sign-in.
  useEffect(() => {
    if (user?.teamId && user?.id) userProfileMarkAccepted(user);
  }, [user?.teamId, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const logout = async () => {
    try {
      // Clear session storage
      clearUserSession();
      
      // Sign out from Appwrite
      await appwriteSignOut();
      
      // Clear local state
      setUser(null);
      setSession(null);
      
      // Force redirect to login
      window.location.href = '/login';
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Failed to log out');
    }
  };

  // Function to refresh user data (call after OAuth callback)
  const refreshUser = async () => {
    try {
      const authSession = await account.getSession('current');
      if (authSession) {
        setSession(authSession);
        const currentUser = await getCurrentUser();
        if (currentUser) {
          setUser(currentUser);
          return currentUser;
        }
      }
      return null;
    } catch (error) {
      console.error('Error refreshing user:', error);
      return null;
    }
  };

  const value = {
    user,
    session,
    logout,
    loading,
    authError,
    refreshUser,
    isAuthenticated: !!user && !!session
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};